<template>
  <div class="medical-signin-wrapper min-vh-100 d-flex align-items-center justify-content-center p-3">
    
    <!-- Background Animated Shapes -->
    <div class="bg-shape shape-1"></div>
    <div class="bg-shape shape-2"></div>

    <div class="card border-0 shadow-lg rounded-4 overflow-hidden w-100 max-w-900 bg-white z-index-1">
      <div class="row g-0">
        
        <!-- Left Side Decorative Branding Panel -->
        <div class="col-12 col-lg-6 bg-gradient-teal p-4 p-md-5 text-white d-flex flex-column justify-content-between position-relative">
          <div class="brand-bg-icon">
            <i class="fas fa-hospital-user"></i>
          </div>

          <div>
            <div class="d-flex align-items-center gap-3 mb-4">
              <div class="logo-icon-box">
                <i class="fas fa-heartbeat text-teal fa-lg"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bolder text-white">MediSign Care</h4>
                <small class="text-white-75 text-xs text-uppercase font-weight-bold">Hospital Signage Portal</small>
              </div>
            </div>

            <h2 class="font-weight-bold text-white mb-3">Healthcare & Queue Management System</h2>
            <p class="text-white-75 text-sm">
              Secure staff sign-in for real-time patient queue control, doctor OPD scheduling, emergency code broadcasts, and lobby TV kiosk management.
            </p>
          </div>

          <!-- Feature Highlights -->
          <div class="mt-4 pt-3 border-top border-white-20">
            <div class="d-flex align-items-center gap-3 mb-2">
              <span class="badge bg-white-20 text-white rounded-circle p-2">
                <i class="fas fa-shield-halved"></i>
              </span>
              <small class="text-white-90 text-xs font-weight-bold">HIPAA Compliant Data & Secure Queue Broadcast</small>
            </div>
            <div class="d-flex align-items-center gap-3">
              <span class="badge bg-white-20 text-white rounded-circle p-2">
                <i class="fas fa-broadcast-tower"></i>
              </span>
              <small class="text-white-90 text-xs font-weight-bold">18 Active Hospital Kiosks Live Stream</small>
            </div>
          </div>
        </div>

        <!-- Right Side Sign In Form -->
        <div class="col-12 col-lg-6 p-4 p-md-5 d-flex flex-column justify-content-center bg-white">
          
          <div class="text-start mb-4">
            <h4 class="font-weight-bolder text-dark mb-1">Staff Sign In</h4>
            <p class="text-muted text-xs">Enter your medical credentials to access the dashboard</p>
          </div>

          <!-- Error Alert (If login fails) -->
          <div v-if="errorMessage" class="alert alert-danger text-white text-xs py-2 px-3 rounded-3 mb-3 d-flex align-items-center justify-content-between">
            <span><i class="fas fa-exclamation-circle me-2"></i>{{ errorMessage }}</span>
            <button type="button" class="btn-close btn-close-white text-xs" @click="errorMessage = ''"></button>
          </div>

          <form @submit.prevent="handleSignIn">
            
            <!-- Email / Staff ID -->
            <div class="mb-3">
              <label class="form-label text-xs font-weight-bold text-dark text-uppercase mb-1">Medical Staff Email / ID</label>
              <div class="input-group input-group-alternative">
                <span class="input-group-text bg-light border-end-0 text-muted">
                  <i class="fas fa-user-md"></i>
                </span>
                <input 
                  type="email" 
                  class="form-control bg-light border-start-0 text-sm" 
                  placeholder="doctor@hospital.com" 
                  v-model="email"
                  required
                />
              </div>
            </div>

            <!-- Password -->
            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="form-label text-xs font-weight-bold text-dark text-uppercase mb-0">Password</label>
                <a href="javascript:;" class="text-teal text-xxs font-weight-bold" @click="forgotPassword">Forgot Password?</a>
              </div>
              <div class="input-group input-group-alternative">
                <span class="input-group-text bg-light border-end-0 text-muted">
                  <i class="fas fa-lock"></i>
                </span>
                <input 
                  :type="showPassword ? 'text' : 'password'" 
                  class="form-control bg-light border-start-0 text-sm" 
                  placeholder="••••••••" 
                  v-model="password"
                  required
                />
                <button 
                  type="button" 
                  class="btn btn-light border border-start-0 text-muted mb-0 px-3" 
                  @click="showPassword = !showPassword"
                >
                  <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
            </div>

            <!-- Department Selection -->
            <div class="mb-3">
              <label class="form-label text-xs font-weight-bold text-dark text-uppercase mb-1">Assigned Department</label>
              <select class="form-select bg-light text-sm" v-model="department">
                <option value="Cardiology">Cardiology OPD</option>
                <option value="Orthopedics">Orthopedics OPD</option>
                <option value="Pediatrics">Pediatrics OPD</option>
                <option value="Radiology">Radiology & MRI</option>
                <option value="Emergency">Emergency & Trauma</option>
                <option value="Administration">Hospital Administration</option>
              </select>
            </div>

            <!-- Remember Me & Kiosk Mode -->
            <div class="d-flex align-items-center justify-content-between mb-4">
              <div class="form-check form-switch ps-0">
                <input class="form-check-input ms-0 me-2" type="checkbox" id="rememberMe" v-model="rememberMe" />
                <label class="form-check-label text-xs text-muted font-weight-medium" for="rememberMe">Remember Session</label>
              </div>
            </div>

            <!-- Submit Button -->
            <button 
              type="submit" 
              class="btn btn-teal text-white font-weight-bold w-100 py-2 rounded-3 shadow-sm mb-3 d-flex align-items-center justify-content-center gap-2"
              :disabled="loading"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <span v-else><i class="fas fa-sign-in-alt me-1"></i> Sign In to Medical Portal</span>
            </button>

            <!-- Quick Demo Auto-fill Button -->
            <button 
              type="button" 
              class="btn btn-outline-secondary text-xs w-100 py-2 rounded-3 mb-0"
              @click="fillDemoCredentials"
            >
              <i class="fas fa-key me-1 text-teal"></i> Auto-fill Demo Credentials (Doctor Admin)
            </button>

          </form>

        </div>

      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: "MedicalSignIn",
  data() {
    return {
      email: "",
      password: "",
      department: "Cardiology",
      rememberMe: true,
      showPassword: false,
      loading: false,
      errorMessage: "",
    };
  },
  beforeMount() {
    // Hide global App sidenav, navbar and footer for a clean login page
    this.$store.state.showSidenav = false;
    this.$store.state.showNavbar = false;
    this.$store.state.showFooter = false;
  },
  beforeUnmount() {
    this.$store.state.showSidenav = true;
    this.$store.state.showNavbar = true;
    this.$store.state.showFooter = true;
  },
  methods: {
    fillDemoCredentials() {
      this.email = "dr.admin@medisign.com";
      this.password = "hospital123";
      this.department = "Cardiology";
      this.errorMessage = "";
    },
    handleSignIn() {
      this.errorMessage = "";
      if (!this.email || !this.password) {
        this.errorMessage = "Please fill in all credentials";
        return;
      }

      this.loading = true;

      // Simulate Authentication API response delay
      setTimeout(() => {
        this.loading = false;
        
        // Store Medical Auth Session in localStorage
        const medicalSession = {
          email: this.email,
          department: this.department,
          token: "med_jwt_token_" + Date.now(),
          loggedInAt: new Date().toISOString(),
        };
        
        localStorage.setItem("medical_authenticated", "true");
        localStorage.setItem("medical_session", JSON.stringify(medicalSession));

        // Navigate directly to Medical Dashboard
        this.$router.push("/medical-dashboard");
      }, 800);
    },
    forgotPassword() {
      alert("Password reset instructions have been sent to your department IT administrator.");
    },
  },
};
</script>

<style scoped>
.medical-signin-wrapper {
  background: #f1f5f9;
  position: relative;
  overflow: hidden;
}

.max-w-900 {
  max-width: 900px;
}

.bg-gradient-teal {
  background: linear-gradient(135deg, #0f766e 0%, #0d9488 60%, #0284c7 100%);
}

.logo-icon-box {
  width: 44px;
  height: 44px;
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.brand-bg-icon {
  position: absolute;
  right: -30px;
  bottom: -40px;
  font-size: 14rem;
  opacity: 0.08;
  color: #ffffff;
  pointer-events: none;
}

.text-teal {
  color: #0d9488 !important;
}

.btn-teal {
  background-color: #0d9488 !important;
  border-color: #0d9488 !important;
}

.btn-teal:hover {
  background-color: #0f766e !important;
}

.bg-white-20 {
  background-color: rgba(255, 255, 255, 0.2);
}

.text-white-75 {
  color: rgba(255, 255, 255, 0.75);
}

.text-white-90 {
  color: rgba(255, 255, 255, 0.9);
}

.border-white-20 {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.bg-shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  z-index: 0;
}

.shape-1 {
  width: 400px;
  height: 400px;
  background: rgba(13, 148, 136, 0.15);
  top: -100px;
  left: -100px;
}

.shape-2 {
  width: 500px;
  height: 500px;
  background: rgba(2, 132, 199, 0.15);
  bottom: -150px;
  right: -150px;
}

.z-index-1 {
  z-index: 1;
}
</style>
